"use client";

import Image from "next/image";
import { createContext, useContext, useEffect, useRef } from "react";
import type { CSSProperties, ReactNode } from "react";
import { animate, useInView, useReducedMotion } from "framer-motion";

import { FADE_EASE } from "@/components/global/utils/FadeIn";

import ringGold from "@/public/leaderboard/ranks/ring-gold.png";
import ringSilver from "@/public/leaderboard/ranks/ring-silver.png";

export type RingTone = "bronze" | "silver" | "gold" | "brightGold";

const RING_TONE: Record<
  RingTone,
  { band: typeof ringGold; filter?: string; arc: [string, string]; needle: string }
> = {
  bronze: {
    band: ringGold,
    filter: "hue-rotate(-16deg) saturate(1.1) brightness(0.93)",
    arc: ["#e0a26a", "#ffffff"],
    needle: "#ffdfc2",
  },
  silver: { band: ringSilver, arc: ["#b3c2c7", "#ffffff"], needle: "#b3c2c7" },
  gold: { band: ringGold, arc: ["#ffc75f", "#ffffff"], needle: "#fffae7" },
  brightGold: {
    band: ringGold,
    filter: "saturate(1.3) brightness(1.08)",
    arc: ["#ffda2c", "#ffffff"],
    needle: "#fffae7",
  },
};

/**
 * The arc is authored counter-clockwise from the 12 o'clock mark, and the
 * pointer is its needle — it rides the arc's leading end. Both numbers come
 * out of the path itself: it spans 262.24°, so a needle parked at the arc's
 * tip has to travel that far back to reach the top.
 */
const SWEEP = 262.24;
const DURATION = 1.4;
/** Degrees of falloff on the band's leading edge, so the metal fades in behind the crisp arc tip. */
const BAND_FEATHER = 12;

/**
 * One trigger drives the arc, the needle and the points readout. They sit in
 * different branches of the card, and giving each its own observer let them
 * start at slightly different scroll offsets — visibly so, since the readout
 * is a short line and the ring is tall.
 */
const RevealContext = createContext(false);

export function RankRevealScope({
  className,
  style,
  onMouseEnter,
  onMouseLeave,
  children,
}: {
  className?: string;
  style?: CSSProperties;
  onMouseEnter?: () => void;
  onMouseLeave?: () => void;
  children: ReactNode;
}) {
  const ref = useRef<HTMLDivElement>(null);
  // Matches FadeIn's own defaults so the sweep and the fade agree on when the
  // card has arrived.
  const shown = useInView(ref, { once: true, amount: 0.2 });

  return (
    <div
      ref={ref}
      className={className}
      style={style}
      onMouseEnter={onMouseEnter}
      onMouseLeave={onMouseLeave}
    >
      <RevealContext.Provider value={shown}>{children}</RevealContext.Provider>
    </div>
  );
}

export function RingPointer({
  className,
  style,
}: {
  className?: string;
  style?: CSSProperties;
}) {
  return (
    <svg viewBox="0 0 14.6667 7.33333" fill="none" aria-hidden className={className} style={style}>
      <path d="M7.33333 0L0 7.33333H14.6667L7.33333 0Z" fill="currentColor" />
    </svg>
  );
}

export function RankProgressArc({
  tone = "gold",
  play = true,
}: {
  tone?: RingTone;
  play?: boolean;
}) {
  const metal = RING_TONE[tone];
  const gradientId = `rank-ring-arc-${tone}`;
  const shown = useContext(RevealContext);
  const reduceMotion = useReducedMotion();
  const arcRef = useRef<SVGPathElement>(null);
  const needleRef = useRef<HTMLDivElement>(null);
  const bandRef = useRef<HTMLDivElement>(null);

  const bandMask = `conic-gradient(from 0deg, #000 max(0deg, calc(var(--reveal) - ${BAND_FEATHER}deg)), transparent var(--reveal))`;

  useEffect(() => {
    const arc = arcRef.current;
    const needle = needleRef.current;
    const band = bandRef.current;
    if (!arc || !needle || !band) return;

    // A negative dash offset reveals the path from its end, which is the 12
    // o'clock origin — so the arc grows clockwise even though it is drawn the
    // other way.
    const draw = (t: number) => {
      arc.style.strokeDashoffset = String(t - 1);
      needle.style.transform = `rotate(${(t - 1) * SWEEP}deg)`;
      band.style.setProperty("--reveal", `${t * SWEEP}deg`);
    };

    // The band carries its own fade towards the gap, and the mask would clip
    // that tail. Dropping the mask at the end leaves the ring exactly as
    // designed; the wisp it restores is under 20% opaque, so it does not pop.
    const settle = () => {
      band.style.maskImage = "none";
      band.style.webkitMaskImage = "none";
    };

    const arm = () => {
      band.style.maskImage = bandMask;
      band.style.webkitMaskImage = bandMask;
    };

    if (reduceMotion) {
      arm();
      draw(play ? 1 : 0);
      if (play) settle();
      return;
    }
    if (!shown || !play) {
      arm();
      draw(0);
      return;
    }

    arm();
    const controls = animate(0, 1, {
      duration: DURATION,
      ease: FADE_EASE,
      onUpdate: draw,
      onComplete: settle,
    });
    return () => controls.stop();
  }, [shown, play, reduceMotion, bandMask]);

  return (
    <>
      <div
        ref={bandRef}
        aria-hidden
        className="pointer-events-none absolute inset-0"
        style={
          {
            "--reveal": "0deg",
            maskImage: bandMask,
            WebkitMaskImage: bandMask,
          } as CSSProperties
        }
      >
        <Image
          src={metal.band}
          alt=""
          fill
          className="pointer-events-none object-contain"
          style={metal.filter ? { filter: metal.filter } : undefined}
        />
      </div>

      <svg
        viewBox="0 0 220 220"
        fill="none"
        aria-hidden
        className="pointer-events-none absolute inset-0 h-full w-full"
      >
        <defs>
          <linearGradient id={gradientId} x1="0" y1="0" x2="0" y2="1">
            <stop stopColor={metal.arc[0]} />
            <stop offset="1" stopColor={metal.arc[1]} />
          </linearGradient>
        </defs>
        <path
          ref={arcRef}
          transform="translate(10.1985 9.2486)"
          d="M1.36204 114.256C3.97187 133.132 11.9589 150.859 24.3695 165.319C36.7801 179.78 53.0901 190.363 71.3521 195.806C89.6142 201.249 109.057 201.321 127.359 196.014C145.661 190.707 162.049 180.246 174.567 165.878C187.085 151.51 195.204 133.844 197.954 114.987C200.704 96.1309 197.97 76.8814 190.077 59.5368C182.184 42.1922 169.467 27.4855 153.443 17.1722C137.419 6.85897 118.766 1.375 99.7099 1.375"
          stroke={`url(#${gradientId})`}
          strokeWidth="2.75"
          pathLength={1}
          strokeDasharray="1 1"
          strokeDashoffset={-1}
        />
      </svg>

      <div
        ref={needleRef}
        aria-hidden
        className="pointer-events-none absolute inset-0"
        style={{ transform: `rotate(${-SWEEP}deg)` }}
      >
        <RingPointer
          className="absolute left-[-2%] top-[57.2%] w-[6.67%] -translate-x-1/2 -translate-y-1/2 rotate-[80.75deg]"
          style={{ color: metal.needle }}
        />
      </div>
    </>
  );
}

/** Counts up alongside the arc. Rendered with the real value so it is correct before hydration. */
export function RankPointsCount({ value }: { value: number }) {
  const shown = useContext(RevealContext);
  const reduceMotion = useReducedMotion();
  const ref = useRef<HTMLSpanElement>(null);

  useEffect(() => {
    const el = ref.current;
    if (!el) return;

    if (reduceMotion) {
      el.textContent = String(value);
      return;
    }
    if (!shown) {
      el.textContent = "0";
      return;
    }

    const controls = animate(0, value, {
      duration: DURATION,
      ease: FADE_EASE,
      // Points render without a thousands separator in this design.
      onUpdate: (v) => {
        el.textContent = String(Math.round(v));
      },
    });
    return () => controls.stop();
  }, [shown, reduceMotion, value]);

  return (
    <span ref={ref} className="tabular-nums">
      {value}
    </span>
  );
}
